import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Autocomplete,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SyncIcon from "@mui/icons-material/Sync";
import { API_URL } from "../config";
import SoftwareDemosPanel from "./SoftwareDemosPanel";
import SoftwareDownloadLogDialog from "./SoftwareDownloadLogDialog";

const CHANNEL_TABS = [
    {
        key: "setup",
        label: "Setup",
        uploadTitle: "Upload Setup (.exe)",
        publicSuffix: "/setup/current",
        legacyPath: "/downloads/machineflow/current",
    },
    {
        key: "updater",
        label: "Updater",
        uploadTitle: "Upload Updater (.exe)",
        publicSuffix: "/updater/current",
    },
    {
        key: "setup_alt",
        label: "Alternate",
        uploadTitle: "Upload Alternate Setup (.exe)",
        publicSuffix: "/alternate/current",
    },
];

function formatBytes(n) {
    const size = Number(n) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function shortSha(sha) {
    if (!sha) return "—";
    return `${sha.slice(0, 10)}…`;
}

function compareVersions(a, b) {
    const left = String(a || "")
        .replace(/^v/i, "")
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map((n) => Number.parseInt(n, 10));
    const right = String(b || "")
        .replace(/^v/i, "")
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map((n) => Number.parseInt(n, 10));
    const len = Math.max(left.length, right.length);
    for (let i = 0; i < len; i += 1) {
        const delta = (left[i] || 0) - (right[i] || 0);
        if (delta) return delta > 0 ? 1 : -1;
    }
    return 0;
}

function channelLabel(channel) {
    if (channel === "setup_alt") return "Alternate";
    if (channel === "updater") return "Updater";
    return "Setup";
}

export default function SoftwareInstallers() {
    const [tab, setTab] = useState(0);
    const isDemosTab = tab === CHANNEL_TABS.length;
    const channelMeta = CHANNEL_TABS[tab] || CHANNEL_TABS[0];
    const channel = channelMeta.key;

    const [items, setItems] = useState([]);
    const [current, setCurrent] = useState(null);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [version, setVersion] = useState("");
    const [notes, setNotes] = useState("");
    const [branchId, setBranchId] = useState("");
    const [audience, setAudience] = useState("all_subscribers");
    const [notifyOnPublish, setNotifyOnPublish] = useState(true);
    const [setAsCurrent, setSetAsCurrent] = useState(true);
    const [file, setFile] = useState(null);
    const [apps, setApps] = useState([]);
    const [appId, setAppId] = useState("");
    const [appDialog, setAppDialog] = useState(false);
    const [appForm, setAppForm] = useState({ displayName: "", slug: "", tagline: "", planMatch: "" });
    const [enableRow, setEnableRow] = useState(null);
    const [enableAudience, setEnableAudience] = useState("all_subscribers");
    const [enableNotify, setEnableNotify] = useState(true);
    const [grantCustomer, setGrantCustomer] = useState(null);
    const [grantEmail, setGrantEmail] = useState("");
    const [grants, setGrants] = useState([]);

    const [customers, setCustomers] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [setupOptions, setSetupOptions] = useState([]);
    const [updaterOptions, setUpdaterOptions] = useState([]);
    const [assignCustomer, setAssignCustomer] = useState(null);
    const [assignSetupId, setAssignSetupId] = useState("");
    const [assignUpdaterId, setAssignUpdaterId] = useState("");
    const [assignNotes, setAssignNotes] = useState("");
    const [logOpen, setLogOpen] = useState(false);
    const [logLoading, setLogLoading] = useState(false);
    const [logTitle, setLogTitle] = useState("Downloads");
    const [logData, setLogData] = useState({ events: [], total: 0, uniqueCount: 0, anonymousCount: 0 });
    const [reportedDevices, setReportedDevices] = useState([]);
    const [currentSetup, setCurrentSetup] = useState(null);
    const [currentUpdater, setCurrentUpdater] = useState(null);

    const apiBase = useMemo(() => String(API_URL || "").replace(/\/$/, ""), []);

    const selectedApp = apps.find((a) => a.id === appId) || apps[0] || null;
    const publicUrl = `${apiBase}/downloads/${selectedApp?.slug || "machineflow"}${channelMeta.publicSuffix}`;
    const legacyUrl =
        channelMeta.legacyPath && selectedApp?.slug === "machineflow" ? `${apiBase}${channelMeta.legacyPath}` : null;

    const loadChannel = useCallback(async () => {
        if (isDemosTab) return;
        setItems([]);
        setCurrent(null);
        const { data } = await axios.get(`${API_URL}/software-installers/list`, {
            withCredentials: true,
            params: { channel, appId: appId || undefined },
        });
        setItems(data.items || []);
        setCurrent(data.current || null);
        if (channel === "setup") setCurrentSetup(data.current || null);
        if (channel === "updater") setCurrentUpdater(data.current || null);
    }, [channel, isDemosTab, appId]);

    const loadReported = useCallback(async () => {
        if (!appId || selectedApp?.slug !== "machineflow") {
            setReportedDevices([]);
            return;
        }
        try {
            const [{ data: deviceData }, updaterRes, setupRes] = await Promise.all([
                axios.get(`${API_URL}/software-licenses/devices`, { withCredentials: true }),
                axios.get(`${API_URL}/software-installers/list`, {
                    withCredentials: true,
                    params: { channel: "updater", appId },
                }),
                axios.get(`${API_URL}/software-installers/list`, {
                    withCredentials: true,
                    params: { channel: "setup", appId },
                }),
            ]);
            setReportedDevices(deviceData.items || []);
            setCurrentUpdater(updaterRes.data.current || null);
            setCurrentSetup(setupRes.data.current || null);
        } catch {
            setReportedDevices([]);
        }
    }, [appId, selectedApp?.slug]);

    const loadAssignments = useCallback(async () => {
        const [{ data: assignData }, { data: customerData }] = await Promise.all([
            axios.get(`${API_URL}/software-installers/assignments`, {
                withCredentials: true,
                params: { appId: appId || undefined },
            }),
            axios.get(`${API_URL}/customers`, { withCredentials: true }),
        ]);
        setAssignments(assignData.items || []);
        setSetupOptions(assignData.setupOptions || []);
        setUpdaterOptions(assignData.updaterOptions || []);
        setCustomers(Array.isArray(customerData) ? customerData : customerData?.customers || []);
    }, [appId]);

    const loadApps = useCallback(async () => {
        const { data } = await axios.get(`${API_URL}/software-installers/apps`, { withCredentials: true });
        const list = data.items || [];
        setApps(list);
        setAppId((prev) => prev || list.find((a) => a.slug === "machineflow")?.id || list[0]?.id || "");
    }, []);

    useEffect(() => {
        loadApps().catch((error) =>
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Unable to load software apps.",
            })
        );
    }, [loadApps]);

    useEffect(() => {
        loadChannel().catch((error) =>
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Unable to load installers.",
            })
        );
    }, [loadChannel]);

    useEffect(() => {
        loadAssignments().catch(() => {
            /* non-fatal on first paint */
        });
    }, [loadAssignments]);

    useEffect(() => {
        loadReported().catch(() => {
            /* non-fatal */
        });
    }, [loadReported]);

    useEffect(() => {
        setVersion("");
        setNotes("");
        setBranchId("");
        setFile(null);
        setSetAsCurrent(true);
        setAudience("all_subscribers");
        setNotifyOnPublish(true);
    }, [channel, appId]);

    async function upload(event) {
        event.preventDefault();
        if (!file) {
            setMessage({ severity: "warning", text: "Choose a .exe file." });
            return;
        }
        if (!version.trim()) {
            setMessage({ severity: "warning", text: "Enter a version (e.g. 1.2.0)." });
            return;
        }
        setBusy(true);
        setMessage(null);
        try {
            const body = new FormData();
            body.append("file", file);
            body.append("version", version.trim());
            body.append("channel", channel);
            if (appId) body.append("appId", appId);
            if (notes.trim()) body.append("notes", notes.trim());
            if (branchId.trim()) body.append("branchId", branchId.trim());
            body.append("audience", audience);
            body.append("notify", notifyOnPublish ? "true" : "false");
            body.append("setCurrent", setAsCurrent ? "true" : "false");
            await axios.post(`${API_URL}/software-installers`, body, {
                withCredentials: true,
                // Let the browser set multipart boundary — do not force Content-Type.
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
            });
            setFile(null);
            setVersion("");
            setNotes("");
            setBranchId("");
            setMessage({ severity: "success", text: `${channelMeta.label} uploaded.` });
            await loadChannel();
            await loadAssignments();
        } catch (error) {
            const status = error.response?.status;
            const apiError = error.response?.data?.error;
            let text = apiError || error.message || "Upload failed.";
            if (!apiError && status === 413) {
                text = "Upload too large for the proxy. Raise nginx client_max_body_size (e.g. 512m).";
            } else if (!apiError && status === 404) {
                text = "Upload API not found — deploy/restart the backend (app-prod) on the droplet.";
            } else if (!apiError && status) {
                text = `Upload failed (HTTP ${status}).`;
            }
            setMessage({
                severity: "error",
                text,
            });
        } finally {
            setBusy(false);
        }
    }

    async function makeCurrent(id, notify = false) {
        setBusy(true);
        try {
            await axios.put(
                `${API_URL}/software-installers/${id}/current`,
                { notify },
                { withCredentials: true }
            );
            setMessage({
                severity: "success",
                text: notify ? "Update enabled and subscribers notified." : "Current installer updated.",
            });
            await loadChannel();
            return true;
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not set current.",
            });
            return false;
        } finally {
            setBusy(false);
        }
    }

    async function saveEnable() {
        if (!enableRow?.id) return;
        setBusy(true);
        try {
            await axios.put(
                `${API_URL}/software-installers/${enableRow.id}`,
                { audience: enableAudience, notes: enableRow.notes, branchId: enableRow.branchId },
                { withCredentials: true }
            );
            const published = await makeCurrent(enableRow.id, enableNotify);
            if (published) setEnableRow(null);
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not enable update.",
            });
            setBusy(false);
        }
    }

    async function openGrants(row) {
        setEnableRow(row);
        setEnableAudience(row.audience || "all_subscribers");
        setEnableNotify(true);
        try {
            const { data } = await axios.get(`${API_URL}/software-installers/${row.id}/grants`, {
                withCredentials: true,
            });
            setGrants(data.items || []);
        } catch {
            setGrants([]);
        }
    }

    async function addRecipient(event) {
        event.preventDefault();
        if (!enableRow?.id) return;
        const email = grantEmail.trim() || grantCustomer?.email || "";
        if (!email && !grantCustomer?.id) {
            setMessage({ severity: "warning", text: "Pick a customer or enter an email." });
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/software-installers/${enableRow.id}/grants`,
                {
                    customerId: grantCustomer?.id || null,
                    email,
                    notify: enableNotify,
                },
                { withCredentials: true }
            );
            setGrants(data.items || []);
            setGrantCustomer(null);
            setGrantEmail("");
            setMessage({ severity: "success", text: "Update enabled for that account." });
            await axios.put(
                `${API_URL}/software-installers/${enableRow.id}`,
                { audience: "assigned_only" },
                { withCredentials: true }
            );
            await loadChannel();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not enable for that user.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function removeGrantRow(id) {
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/software-installers/grants/${id}`, { withCredentials: true });
            setGrants((prev) => prev.filter((g) => g.id !== id));
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not remove recipient.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function createAppRecord(event) {
        event.preventDefault();
        if (!appForm.displayName.trim()) return;
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/software-installers/apps`,
                appForm,
                { withCredentials: true }
            );
            setAppDialog(false);
            setAppForm({ displayName: "", slug: "", tagline: "", planMatch: "" });
            await loadApps();
            if (data.item?.id) setAppId(data.item.id);
            setMessage({ severity: "success", text: `${data.item.displayName} added.` });
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not create app.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function remove(id) {
        if (!window.confirm("Delete this installer build from the server?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/software-installers/${id}`, { withCredentials: true });
            setMessage({ severity: "success", text: "Installer deleted." });
            await loadChannel();
            await loadAssignments();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not delete.",
            });
        } finally {
            setBusy(false);
        }
    }

    function downloadAdmin(id) {
        window.open(`${API_URL}/software-installers/${id}/download`, "_blank", "noopener,noreferrer");
    }

    async function openDownloadLog(row) {
        setLogTitle(`Downloads · ${channelLabel(row.channel)} v${row.version}`);
        setLogOpen(true);
        setLogLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/software-installers/${row.id}/downloads`, {
                withCredentials: true,
            });
            setLogData(data);
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not load download log.",
            });
            setLogData({ events: [], total: 0, uniqueCount: 0, anonymousCount: 0 });
        } finally {
            setLogLoading(false);
        }
    }

    async function copyText(text, successMsg) {
        try {
            await navigator.clipboard.writeText(text);
            setMessage({ severity: "success", text: successMsg || "Copied." });
        } catch {
            setMessage({ severity: "info", text });
        }
    }

    async function saveAssignment(event) {
        event.preventDefault();
        if (!assignCustomer?.id) {
            setMessage({ severity: "warning", text: "Pick a customer." });
            return;
        }
        if (!assignSetupId && !assignUpdaterId) {
            setMessage({ severity: "warning", text: "Choose at least a Setup or Updater build." });
            return;
        }
        setBusy(true);
        try {
            await axios.put(
                `${API_URL}/software-installers/assignments`,
                {
                    customerId: assignCustomer.id,
                    email: assignCustomer.email || null,
                    setupInstallerId: assignSetupId || null,
                    updaterInstallerId: assignUpdaterId || null,
                    notes: assignNotes.trim() || null,
                    appId: appId || null,
                },
                { withCredentials: true }
            );
            setAssignCustomer(null);
            setAssignSetupId("");
            setAssignUpdaterId("");
            setAssignNotes("");
            setMessage({ severity: "success", text: "Customer override saved." });
            await loadAssignments();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not save override.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function removeAssignment(id) {
        if (!window.confirm("Remove this customer override?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/software-installers/assignments/${id}`, {
                withCredentials: true,
            });
            setMessage({ severity: "success", text: "Override removed." });
            await loadAssignments();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not remove override.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function rotateToken(id) {
        if (!window.confirm("Rotate download token? Existing customer links will stop working.")) return;
        setBusy(true);
        try {
            await axios.post(
                `${API_URL}/software-installers/assignments/${id}/rotate-token`,
                {},
                { withCredentials: true }
            );
            setMessage({ severity: "success", text: "Token rotated." });
            await loadAssignments();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Could not rotate token.",
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Inventory2Icon color="primary" />
                <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">
                        Software · Administration
                    </Typography>
                    <Typography variant="h5">App installers</Typography>
                </Stack>
            </Stack>

            {message ? (
                <Alert severity={message.severity} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            ) : null}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel id="software-app-label">Application</InputLabel>
                            <Select
                                labelId="software-app-label"
                                label="Application"
                                value={appId}
                                onChange={(e) => setAppId(e.target.value)}
                            >
                                {apps.map((app) => (
                                    <MenuItem key={app.id} value={app.id}>
                                        {app.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={() => setAppDialog(true)}>
                            Add application
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Releases, downloads, and account pages are per app. MachineFlow is the default.
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
            >
                {CHANNEL_TABS.map((t) => (
                    <Tab key={t.key} label={t.label} />
                ))}
                <Tab label="Demos" />
            </Tabs>

            {isDemosTab ? (
                <SoftwareDemosPanel
                    customers={customers}
                    setMessage={setMessage}
                    busy={busy}
                    setBusy={setBusy}
                />
            ) : (
                <>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle1">Public {channelMeta.label} URL</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Serves the build marked <strong>Current</strong> for this channel.
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                    <TextField size="small" fullWidth value={publicUrl} InputProps={{ readOnly: true }} />
                                    <Button
                                        variant="outlined"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => copyText(publicUrl, "Public URL copied.")}
                                    >
                                        Copy
                                    </Button>
                                </Stack>
                                {legacyUrl ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Legacy alias (same file): {legacyUrl}
                                    </Typography>
                                ) : null}
                                {current ? (
                                    <Chip
                                        color="success"
                                        icon={<StarIcon />}
                                        label={`Current: v${current.version} · ${formatBytes(current.fileSize)}`}
                                    />
                                ) : (
                                    <Chip label={`No current ${channelMeta.label} published yet`} />
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack component="form" spacing={2} onSubmit={upload}>
                                <Typography variant="subtitle1">{channelMeta.uploadTitle}</Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            label="Version"
                                            placeholder="1.6.0"
                                            required
                                            fullWidth
                                            value={version}
                                            onChange={(e) => setVersion(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 3 }}>
                                        <TextField
                                            label="Branch ID"
                                            placeholder="stable / custom-shop"
                                            fullWidth
                                            value={branchId}
                                            onChange={(e) => setBranchId(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Release notes"
                                            fullWidth
                                            multiline
                                            minRows={1}
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <FormControl>
                                            <FormLabel>Enable as update for</FormLabel>
                                            <RadioGroup
                                                row
                                                value={audience}
                                                onChange={(e) => setAudience(e.target.value)}
                                            >
                                                <FormControlLabel
                                                    value="all_subscribers"
                                                    control={<Radio />}
                                                    label="All subscribed users"
                                                />
                                                <FormControlLabel
                                                    value="assigned_only"
                                                    control={<Radio />}
                                                    label="Assigned users only (custom builds)"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                                            {file ? file.name : "Choose .exe"}
                                            <input
                                                hidden
                                                type="file"
                                                accept=".exe,application/vnd.microsoft.portable-executable"
                                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                            />
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={setAsCurrent}
                                                    onChange={(e) => setSetAsCurrent(e.target.checked)}
                                                />
                                            }
                                            label="Publish as the current update after upload"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={notifyOnPublish}
                                                    onChange={(e) => setNotifyOnPublish(e.target.checked)}
                                                    disabled={!setAsCurrent}
                                                />
                                            }
                                            label="Notify subscribed users"
                                        />
                                    </Grid>
                                </Grid>
                                <Stack direction="row" spacing={1}>
                                    <Button type="submit" variant="contained" disabled={busy}>
                                        Upload
                                    </Button>
                                    <Button
                                        variant="text"
                                        startIcon={<RefreshIcon />}
                                        disabled={busy}
                                        onClick={() =>
                                            Promise.all([loadChannel(), loadAssignments()]).catch((error) =>
                                                setMessage({
                                                    severity: "error",
                                                    text: error.response?.data?.error || "Refresh failed.",
                                                })
                                            )
                                        }
                                    >
                                        Refresh
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={1.25}>
                                <Typography variant="subtitle1">Reported installs</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Versions MachineFlow last reported from each subscriber PC. Compared to current
                                    updater {currentUpdater?.version ? `v${currentUpdater.version}` : "(none published)"}
                                    and installer {currentSetup?.version ? `v${currentSetup.version}` : "(none published)"}.
                                </Typography>
                                {reportedDevices.filter((row) => row.device?.appVersion).length === 0 ? (
                                    <Typography color="text.secondary">
                                        No app versions reported yet. They appear after a licensed PC checks in.
                                    </Typography>
                                ) : (
                                    reportedDevices
                                        .filter((row) => row.device?.appVersion)
                                        .map((row) => {
                                            const installed = row.device.appVersion;
                                            const target = currentUpdater?.version || currentSetup?.version;
                                            const outdated =
                                                target && compareVersions(installed, target) < 0;
                                            return (
                                                <Stack
                                                    key={row.licenseId || row.email}
                                                    direction={{ xs: "column", sm: "row" }}
                                                    spacing={1}
                                                    alignItems={{ sm: "center" }}
                                                    justifyContent="space-between"
                                                >
                                                    <Typography variant="body2">
                                                        {row.email} · installed v{installed}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        color={outdated ? "warning" : "success"}
                                                        label={
                                                            outdated
                                                                ? `Out of date vs ${channelLabel(currentUpdater ? "updater" : "setup")} v${target}`
                                                                : "Up to date"
                                                        }
                                                    />
                                                </Stack>
                                            );
                                        })
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent sx={{ overflowX: "auto" }}>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                                {channelMeta.label} versions
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Version</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Audience</TableCell>
                                        <TableCell>Size</TableCell>
                                        <TableCell>Downloads</TableCell>
                                        <TableCell>Uploaded</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography color="text.secondary">No builds uploaded yet.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((row) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography fontWeight={700}>{row.version}</Typography>
                                                        {row.isCurrent ? (
                                                            <Chip size="small" color="success" label="Current" />
                                                        ) : null}
                                                    </Stack>
                                                    {row.notes ? (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {row.notes}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>{row.branchId || "—"}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            row.audience === "assigned_only"
                                                                ? "Assigned users"
                                                                : "All subscribers"
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{formatBytes(row.fileSize)}</TableCell>
                                                <TableCell>
                                                    <Button size="small" onClick={() => openDownloadLog(row)}>
                                                        {row.downloadCount || 0} {channelLabel(row.channel).toLowerCase()}
                                                        {row.uniqueDownloaders
                                                            ? ` · ${row.uniqueDownloaders} people`
                                                            : ""}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        title="Download"
                                                        onClick={() => downloadAdmin(row.id)}
                                                        disabled={busy}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                    <Button size="small" onClick={() => openGrants(row)} disabled={busy}>
                                                        Enable
                                                    </Button>
                                                    {!row.isCurrent ? (
                                                        <IconButton
                                                            title="Delete"
                                                            color="error"
                                                            onClick={() => remove(row.id)}
                                                            disabled={busy}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack spacing={0.5}>
                                    <Typography variant="subtitle1">Customer overrides</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Assign a specific Setup (stable or alternate) and/or Updater. Paste the private token
                                        URLs into that customer&apos;s email — they do not fall back to global current.
                                    </Typography>
                                </Stack>

                                <Stack component="form" spacing={2} onSubmit={saveAssignment}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Autocomplete
                                                options={customers}
                                                value={assignCustomer}
                                                onChange={(_e, v) => setAssignCustomer(v)}
                                                getOptionLabel={(c) =>
                                                    c ? `${c.name || "Customer"}${c.email ? ` · ${c.email}` : ""}` : ""
                                                }
                                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Customer" size="small" />
                                                )}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="assign-setup-label">Setup build</InputLabel>
                                                <Select
                                                    labelId="assign-setup-label"
                                                    label="Setup build"
                                                    value={assignSetupId}
                                                    onChange={(e) => setAssignSetupId(e.target.value)}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    {setupOptions.map((opt) => (
                                                        <MenuItem key={opt.id} value={opt.id}>
                                                            {channelLabel(opt.channel)} v{opt.version}
                                                            {opt.isCurrent ? " (current)" : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="assign-updater-label">Updater build</InputLabel>
                                                <Select
                                                    labelId="assign-updater-label"
                                                    label="Updater build"
                                                    value={assignUpdaterId}
                                                    onChange={(e) => setAssignUpdaterId(e.target.value)}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    {updaterOptions.map((opt) => (
                                                        <MenuItem key={opt.id} value={opt.id}>
                                                            Updater v{opt.version}
                                                            {opt.isCurrent ? " (current)" : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                label="Notes (optional)"
                                                size="small"
                                                fullWidth
                                                value={assignNotes}
                                                onChange={(e) => setAssignNotes(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Button type="submit" variant="contained" disabled={busy} sx={{ alignSelf: "flex-start" }}>
                                        Save override
                                    </Button>
                                </Stack>

                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Setup</TableCell>
                                            <TableCell>Updater</TableCell>
                                            <TableCell>Private URLs</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {assignments.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Typography color="text.secondary">No customer overrides yet.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            assignments.map((row) => {
                                                const setupFull = `${apiBase}${row.setupUrl}`;
                                                const updaterFull = `${apiBase}${row.updaterUrl}`;
                                                return (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell>
                                                            <Typography fontWeight={600}>
                                                                {row.customerName || row.customerId}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {row.email || "—"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.setupInstallerId
                                                                ? `${channelLabel(row.setupChannel)} v${row.setupVersion}`
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.updaterInstallerId ? `v${row.updaterVersion}` : "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.setupInstallerId ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>
                                                                        …/setup
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        title="Copy Setup URL"
                                                                        onClick={() =>
                                                                            copyText(setupFull, "Customer Setup URL copied.")
                                                                        }
                                                                    >
                                                                        <ContentCopyIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Stack>
                                                            ) : null}
                                                            {row.updaterInstallerId ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>
                                                                        …/updater
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        title="Copy Updater URL"
                                                                        onClick={() =>
                                                                            copyText(
                                                                                updaterFull,
                                                                                "Customer Updater URL copied."
                                                                            )
                                                                        }
                                                                    >
                                                                        <ContentCopyIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Stack>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                title="Rotate token"
                                                                onClick={() => rotateToken(row.id)}
                                                                disabled={busy}
                                                            >
                                                                <SyncIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                title="Remove"
                                                                color="error"
                                                                onClick={() => removeAssignment(row.id)}
                                                                disabled={busy}
                                                            >
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </Stack>
                        </CardContent>
                    </Card>
                </>
            )}

            <Dialog open={appDialog} onClose={() => setAppDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add software application</DialogTitle>
                <DialogContent>
                    <Stack component="form" id="new-app-form" spacing={2} sx={{ mt: 1 }} onSubmit={createAppRecord}>
                        <TextField
                            label="Name"
                            required
                            value={appForm.displayName}
                            onChange={(e) => setAppForm({ ...appForm, displayName: e.target.value })}
                        />
                        <TextField
                            label="Slug"
                            placeholder="machineflow"
                            helperText="Used in download URLs and /account/apps/slug"
                            value={appForm.slug}
                            onChange={(e) => setAppForm({ ...appForm, slug: e.target.value })}
                        />
                        <TextField
                            label="Tagline"
                            value={appForm.tagline}
                            onChange={(e) => setAppForm({ ...appForm, tagline: e.target.value })}
                        />
                        <TextField
                            label="Plan match"
                            placeholder="machineflow"
                            helperText="Matches Wix plan names for subscribers of this app"
                            value={appForm.planMatch}
                            onChange={(e) => setAppForm({ ...appForm, planMatch: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAppDialog(false)}>Cancel</Button>
                    <Button type="submit" form="new-app-form" variant="contained" disabled={busy}>
                        Add app
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(enableRow)} onClose={() => setEnableRow(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    Enable {channelLabel(enableRow?.channel)} v{enableRow?.version}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Publish this file as the update for all subscribers, or only for specific accounts that
                            need a custom build.
                        </Typography>
                        <TextField
                            label="Branch ID"
                            size="small"
                            value={enableRow?.branchId || ""}
                            onChange={(e) =>
                                setEnableRow((row) => (row ? { ...row, branchId: e.target.value } : row))
                            }
                        />
                        <TextField
                            label="Release notes"
                            size="small"
                            multiline
                            minRows={2}
                            value={enableRow?.notes || ""}
                            onChange={(e) =>
                                setEnableRow((row) => (row ? { ...row, notes: e.target.value } : row))
                            }
                        />
                        <FormControl>
                            <FormLabel>Audience</FormLabel>
                            <RadioGroup
                                value={enableAudience}
                                onChange={(e) => setEnableAudience(e.target.value)}
                            >
                                <FormControlLabel
                                    value="all_subscribers"
                                    control={<Radio />}
                                    label="All subscribed users"
                                />
                                <FormControlLabel
                                    value="assigned_only"
                                    control={<Radio />}
                                    label="Assigned users only"
                                />
                            </RadioGroup>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={enableNotify}
                                    onChange={(e) => setEnableNotify(e.target.checked)}
                                />
                            }
                            label="Notify recipients"
                        />
                        <Stack component="form" spacing={1.25} onSubmit={addRecipient}>
                            <Typography variant="subtitle2">Add a specific account</Typography>
                            <Autocomplete
                                options={customers}
                                value={grantCustomer}
                                onChange={(_e, v) => setGrantCustomer(v)}
                                getOptionLabel={(c) =>
                                    c ? `${c.name || "Customer"}${c.email ? ` · ${c.email}` : ""}` : ""
                                }
                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Customer" size="small" />
                                )}
                            />
                            <TextField
                                size="small"
                                label="Or email"
                                value={grantEmail}
                                onChange={(e) => setGrantEmail(e.target.value)}
                            />
                            <Button type="submit" variant="outlined" disabled={busy} sx={{ alignSelf: "flex-start" }}>
                                Enable for this user
                            </Button>
                        </Stack>
                        {grants.length ? (
                            <Stack spacing={0.5}>
                                {grants.map((grant) => (
                                    <Stack
                                        key={grant.id}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="body2">
                                            {grant.customerName || grant.email}
                                        </Typography>
                                        <Button size="small" color="error" onClick={() => removeGrantRow(grant.id)}>
                                            Remove
                                        </Button>
                                    </Stack>
                                ))}
                            </Stack>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnableRow(null)}>Close</Button>
                    <Button variant="contained" onClick={saveEnable} disabled={busy}>
                        Publish update
                    </Button>
                </DialogActions>
            </Dialog>

            <SoftwareDownloadLogDialog
                open={logOpen}
                onClose={() => setLogOpen(false)}
                title={logTitle}
                loading={logLoading}
                total={logData.total}
                uniqueCount={logData.uniqueCount}
                anonymousCount={logData.anonymousCount}
                events={logData.events}
            />
        </Stack>
    );
}
